import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { RestrictedLayout } from "src/components/layouts";
import {
    GroupTrialDetailsBreadcrumbs,
    TrialGroupDetailsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    GroupTrialSubMenu,
    TrialGroupSubMenu,
} from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupActions from "../../../actions/trialGroup";
import { NotificationAlert } from "../../../components/common/index";
import { DatePickerFormInputFabric } from "../../../components/form";
import {
    CheckboxFormInput,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import Link from "src/components/routing/Link";
import "./TrialGroupPage.scss";

interface TrialGroupPageProps {
    form: Dtos.TrialGroup & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialGroup: Dtos.TrialGroup;
    lookups: Dtos.Lookup[];
    loadingTrialGroup: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    savingTrialGroup: boolean;
    saveTrialGroupSuccess: boolean;
    saveTrialGroupFailure: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    saveTrialGroup: TrialGroupActions.ITrialGroupSaveActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroup";

const TrialGroupPage: FC<TrialGroupPageProps> = ({
    form,
    formState,
    formProperties,
    trialGroup,
    lookups,
    loadingTrialGroup,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    savingTrialGroup,
    saveTrialGroupSuccess,
    saveTrialGroupFailure,
    validationFailures,
    permissions,
    clearTrialGroup,
    loadTrialGroup,
    saveTrialGroup,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const { trialId } = useParams<{ trialId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);
    const trialIdNumber = parseInt(trialId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroup = {} as Dtos.TrialGroup;

        loadForm(reduxFormName, Object.assign({ ...trialGroup }, extraData));
    };


    // Load trial group
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        let breadcrumbSetter = undefined;
        let drawerItemSetter = undefined;

        if (trialGroupIdNumber && !trialGroup && !loadingTrialGroup) {
            breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupDetailsBreadcrumbs
                    : GroupTrialDetailsBreadcrumbs;

            drawerItemSetter =
                baseRoute === "trials" ? TrialGroupSubMenu : GroupTrialSubMenu;

            setDrawerItems(
                drawerItemSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber
                )
            );
            loadTrialGroup(trialGroupIdNumber);
        } else if (trialGroup && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        trialGroup,
        setDrawerItems,
        TrialGroupSubMenu,
        GroupTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialGroupDetailsBreadcrumbs,
        GroupTrialDetailsBreadcrumbs,
        TrialGroupSubMenu,
        GroupTrialSubMenu,
        loadTrialGroup,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialGroup,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
            clearTrialGroup();
        }
    }, [
        loadTrialGroupFailure,
        loadTrialGroupErrorCode,
        navigate,
        clearTrialGroup,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialGroupIdNumber && trialGroup) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupDetailsBreadcrumbs
                    : GroupTrialDetailsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    trialGroup?.group?.name,
                    trialGroup?.trial?.studyAcronym
                )
            );
        }
    }, [
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        trialGroup,
        location,
        TrialGroupDetailsBreadcrumbs,
        GroupTrialDetailsBreadcrumbs,
        setBreadcrumbs,
    ]);

    // Reload trial group after saving
    useEffect(() => {
        if (!savingTrialGroup && saveTrialGroupSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const baseRoute = location?.pathname?.split("/")[1];

                const url: string =
                    baseRoute === "trials"
                        ? `/trials/${trialId}/groups`
                        : `/groups/${groupId}/trials`;
                navigate(url);
            } else if (!trialGroupIdNumber) {
                const baseRoute = location?.pathname?.split("/")[1];

                const url: string =
                    baseRoute === "trials"
                        ? `/trials/${trialId}/groups/${groupId}/trialgroup/${trialGroup?.id}/details`
                        : `/groups/${groupId}/trials/${trialId}/trialgroup/${trialGroup?.id}/details`;
                navigate(url);
                clearTrialGroup();
            }
        }
    }, [
        savingTrialGroup,
        saveTrialGroupSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialGroup && saveTrialGroupFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialGroup, saveTrialGroupFailure]);

    // Cleanup effect to clear the trial group on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroup();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroup, resetForm, reduxFormName]);

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url: string =
            baseRoute === "trials"
                ? `/trials/${trialId}/groups`
                : `/groups/${groupId}/trials`;

        navigate(url);
    };

    const getPrimaryLink = () => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url: string =
            baseRoute === "trials"
                ? `/groups/${groupId}/details`
                : `/trials/${trialId}/details`;

        return url;
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroup) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (data) {
            let changes: Dtos.TrialGroup = {} as Dtos.TrialGroup;

            await saveTrialGroup(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const renderContent = useMemo(() => {
        const trialGroupId = trialGroup?.group?.id;
        const trialGroupIds =
            trialGroup?.trial?.trialGroups
                ?.filter((x) => x.parentId == null)
                ?.map((y) => y.groupId) || [];
        const lookupOptions = findLookup("trialGroup", "parentId", lookups);
        const filteredOptions = generateOptionsFromLookup(
            lookupOptions,
            form?.parentId
        )?.filter(
            (option) =>
                option.value !== trialGroupId &&
                trialGroupIds.includes(option.value)
        );

        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialGroupUpdate,
                Dtos.Permission.TrialGroupCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroup ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            parentId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    trialGroup?.children
                                                        ?.length > 0,
                                                inputProps: {
                                                    options: filteredOptions,
                                                    clearable: true,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            administratesForGroupMembers: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Administrates for Group Members",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            status: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "trialGroup",
                                                                "status",
                                                                lookups
                                                            ),
                                                            form?.status
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            dateActivated: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4 mb-0",
                                                fieldClassName:
                                                    "col-sm-7 generic-form-field mb-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            eoiGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>EOI</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            dateInvite: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2 mb-0",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field mb-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            emailGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Additional Email Notifications</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            registrationEmails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value:
                                                        form?.registrationEmails ??
                                                        null,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            randomisationEmails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value:
                                                        form?.randomisationEmails ??
                                                        null,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            screeningEmails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value:
                                                        form?.screeningEmails ??
                                                        null,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            eligibilityEmails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value:
                                                        form?.eligibilityEmails ??
                                                        null,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroup",
                                        {
                                            saeEmails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value:
                                                        form?.saeEmails ?? null,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Details</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.GroupView
                        ) && trialGroupIdNumber ? (
                            <Link
                                className={"btn btn-primary"}
                                url={getPrimaryLink()}
                                target="_blank"
                            >
                                {`Primary  `}
                                <FontAwesomeIcon
                                    icon={
                                        FontAwesomeIcons.Duotone
                                            .ARROWUPRIGHTFROMSQUARE
                                    }
                                />
                            </Link>
                        ) : null}
                    </div>
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialGroup",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        trialGroup,
        findLookup,
        lookups,
        generateOptionsFromLookup,
        form,
        permissions,
        savingTrialGroup,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <RestrictedLayout
            loading={loadingTrialGroup}
            permission={Dtos.Permission.TrialGroupView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialGroupPageProps> => {
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroup: trialGroup!,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
        savingTrialGroup:
            state.trialGroup.saveState &&
            state.trialGroup.saveState.status == RequestState.Pending,
        saveTrialGroupSuccess:
            state.trialGroup.saveState &&
            state.trialGroup.saveState.status == RequestState.Success,
        saveTrialGroupFailure:
            state.trialGroup.saveState &&
            state.trialGroup.saveState.status == RequestState.Failure,
        permissions: permissions,
        lookups: state.trialGroup.lookups,
        form: state.reduxForms.trialGroup,
        formState: state.trialGroup.formState,
        formProperties: state.trialGroup.formProperties,
        validationFailures: trialGroup
            ? state.trialGroup.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch),
        saveTrialGroup: bindActionCreators(
            TrialGroupActions.SaveTrialGroup,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupPage);
